/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { App, Dropdown, Tooltip } from 'antd'
import { formatPhoneNumber } from 'react-phone-number-input'
import {
  MailOutlined,
  PhoneOutlined,
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightMedium,
  weightRegular,
  paragraphLarge,
  paragraphXSmall,
} from 'src/components/css'
import type { FamilyMember } from '../../hooks/useFamilyMembers'

interface Props {
  member: FamilyMember
  /**
   * Show the ⋮ edit/remove menu. Only true when the current user is PRIMARY
   * (admin) for the player whose family this is — otherwise the row is
   * view-only (contact copy actions still show).
   */
  showActions?: boolean
  onEdit: (member: FamilyMember) => void
  onDelete: (member: FamilyMember) => void
  onResend: (member: FamilyMember) => void
}

const FamilyMemberRow: React.FC<Props> = ({
  member,
  showActions = false,
  onEdit,
  onDelete,
  onResend,
}) => {
  const { message } = App.useApp()

  const copyToClipboard = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value)
      message.success(`${label} copied to clipboard`)
    } catch {
      message.error(`Couldn't copy ${label.toLowerCase()}`)
    }
  }

  const menuItems: MenuProps['items'] = [
    ...(!member.isAccepted
      ? [
          {
            key: 'resend',
            icon: <MailOutlined />,
            label: capitalizeWords('Resend Invite'),
            onClick: () => onResend(member),
          },
        ]
      : []),
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: capitalizeWords('Edit Info'),
      onClick: () => onEdit(member),
    },
    {
      key: 'remove',
      icon: <DeleteOutlined />,
      label: capitalizeWords('Remove'),
      danger: true,
      onClick: () => onDelete(member),
    },
  ]

  // Second row reads: relation · email · phone (phone only when present),
  // joined by muted separator dots.
  const detailParts: React.ReactNode[] = []
  if (member.relationshipLabel) {
    detailParts.push(<span key='relation'>{member.relationshipLabel}</span>)
  }
  if (member.email) {
    detailParts.push(<span key='email'>{member.email}</span>)
  }
  if (member.phone) {
    // Match the app's other list/detail views — display the number in national
    // format, e.g. "(604) 353-8877". Fall back to the raw value if it can't be
    // parsed.
    detailParts.push(
      <span key='phone'>{formatPhoneNumber(member.phone) || member.phone}</span>
    )
  }

  return (
    <div css={rowStyle}>
      <div css={infoColStyle}>
        <span css={nameRowStyle}>
          <span css={nameStyle}>{member.name}</span>
          {member.status ? (
            <span
              css={[
                statusChipStyle,
                member.isAccepted ? chipAcceptedStyle : chipPendingStyle,
              ]}
            >
              <span css={chipDotStyle} aria-hidden='true' />
              {capitalizeWords(member.status)}
            </span>
          ) : null}
          {member.isPrimary ? (
            <span css={[statusChipStyle, chipAdminStyle]}>
              {capitalizeWords('Admin')}
            </span>
          ) : null}
        </span>
        {detailParts.length > 0 ? (
          <span css={detailStyle}>
            {detailParts.map((part, i) => (
              <React.Fragment key={i}>
                {i > 0 ? (
                  <span
                    css={[dotStyle, separatorDotStyle]}
                    aria-hidden='true'
                  />
                ) : null}
                {part}
              </React.Fragment>
            ))}
          </span>
        ) : null}
      </div>

      <div css={iconsRowStyle}>
        {/* Phone + email copy buttons only show once the invite is accepted;
            an invited/pending contact has no usable contact actions yet. Order
            left→right: phone, email, ⋮. Tapping phone/email copies it. */}
        {member.isAccepted && member.phone ? (
          <Tooltip
            title={capitalizeWords('Copy Phone Number')}
            /* The modal wrapper sits at zIndex 10001; the tooltip portals out,
               so without a higher zIndex it renders behind the modal. */
            zIndex={10050}
          >
            <button
              type='button'
              css={iconButtonStyle}
              onClick={() =>
                copyToClipboard(member.phone as string, 'Phone number')
              }
              aria-label={`Copy ${member.name}'s phone number`}
            >
              <PhoneOutlined css={phoneIconStyle} />
            </button>
          </Tooltip>
        ) : null}

        {member.isAccepted && member.email ? (
          <Tooltip title={capitalizeWords('Copy Email')} zIndex={10050}>
            <button
              type='button'
              css={iconButtonStyle}
              onClick={() => copyToClipboard(member.email as string, 'Email')}
              aria-label={`Copy ${member.name}'s email`}
            >
              <MailOutlined css={mailIconActiveStyle} />
            </button>
          </Tooltip>
        ) : null}

        {/* Edit/remove menu — only when the current user is primary (admin) for
            this player. Non-primary viewers see the row without the ⋮. */}
        {showActions ? (
          <Dropdown
            menu={{ items: menuItems }}
            trigger={['click']}
            placement='bottomRight'
            /* Render above the Family Management modal (wrapper zIndex 10001);
               default Ant dropdown zIndex (~1050) would hide it behind the modal. */
            overlayStyle={{ zIndex: 10050 }}
          >
            <button
              css={moreButtonStyle}
              type='button'
              aria-label={`More options for ${member.name}`}
            >
              <MoreOutlined css={moreIconStyle} />
            </button>
          </Dropdown>
        ) : null}
      </div>
    </div>
  )
}

export default FamilyMemberRow

// ─── Styles ────────────────────────────────────────────────────────────────

const rowStyle = css`
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`

const infoColStyle = css`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

// Name + status chip sit on the first line.
const nameRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`

const nameStyle = css`
  ${paragraphLarge}
  ${weightMedium}
  ${font.body}
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Status chip next to the name: tinted pill + colored dot + label. Green when
// accepted, warning yellow while pending.
const statusChipStyle = css`
  ${paragraphXSmall}
  ${weightMedium}
  ${font.body}
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px;
  border-radius: 4px;
  flex-shrink: 0;
`

const chipAcceptedStyle = css`
  color: ${colors.SUCCESS_300};
  background-color: rgba(16, 173, 50, 0.16);
`

const chipPendingStyle = css`
  color: ${colors.WARNING_300};
  background-color: rgba(255, 193, 7, 0.16);
`

// Admin chip flags a contact with administrative access to the player.
const chipAdminStyle = css`
  color: ${colors.INFO_300};
  background-color: rgba(56, 148, 255, 0.16);
`

// The dot inside the chip inherits the chip text color.
const chipDotStyle = css`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: currentColor;
`

// Single-line detail: relation · email · phone. Never wraps — the line stays
// on one row and truncates the overflowing tail with an ellipsis so the row
// height is stable. Rendered as inline text (not flex) so the whole line is one
// truncatable unit; the separator dots are inline-block so they flow with it.
const detailStyle = css`
  ${paragraphXSmall}
  ${weightRegular}
  ${font.body}
  font-size: 14px;
  color: ${colors.NEUTRAL_50};
  display: block;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// Separator dots between relation · email · phone. Inline-block so they sit on
// the single detail line and don't introduce a flex break.
const dotStyle = css`
  display: inline-block;
  vertical-align: middle;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  margin: 0 6px;
`

const separatorDotStyle = css`
  background-color: ${colors.NEUTRAL_100};
`

const iconsRowStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`

// Icon-only copy button — bare reset so it reads as the icon, not a button.
const iconButtonStyle = css`
  line-height: 0;
  display: flex;
  align-items: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`

const mailIconActiveStyle = css`
  font-size: 24px;
  color: ${colors.PRIMARY};
`

const phoneIconStyle = css`
  font-size: 24px;
  color: ${colors.PRIMARY};
`

const moreIconStyle = css`
  font-size: 24px;
  color: ${colors.PRIMARY};
`

const moreButtonStyle = css`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 0;
  display: flex;
  align-items: center;
`
